import {
    forwardRef,
    KeyboardEvent,
    useImperativeHandle,
    useRef,
    useState,
    useCallback,
} from 'react';
import { useParams } from 'react-router-dom';
import { RenderIf } from 'react-rainbow-components';
import { isEmpty } from '@rainbow-modules/validation';
import AttachFileButton from 'components/AttachFileButton';
import AttachedFiles from 'components/AttachedFiles';
import HelpMeWrite from 'components/HelpMeWrite';
import MicrophoneIcon from 'components/icons/microphone';
import FrequentMessage from 'components/FrequentMessage';
import DictateForm from 'components/SendNoteForm/DictateForm';
import useMultipartMutation from 'data/hooks/useMultipartMutation';
import { IMAGE_FORMATS_ACCEPTED_STRING } from '../../constants';
import {
    Container, InputContainer,
    MessageInput, ActionsContainer, ActionButton,
} from '../SendNoteForm/styled';

interface FeedbackInputProps {
    value: string;
    onChange: (value: string) => void;
    attachedFiles: File[];
    onAttachFiles: (filesList: FileList | null) => void;
    onRemoveFile: (index: number) => void;
    placeholder?: string;
    mode?: 'notes' | 'messages';
    disabled?: boolean;
}

export interface FeedbackInputRef {
    focus: () => void;
}

const FeedbackInput = forwardRef<FeedbackInputRef, FeedbackInputProps>(
    ({
        value,
        onChange,
        attachedFiles,
        onAttachFiles,
        onRemoveFile,
        placeholder = 'Describe the issue or provide feedback...',
        mode = 'messages',
        disabled = false,
    }, ref) => {
        const inputRef = useRef<HTMLTextAreaElement>();
        const { agentId = '' } = useParams();
        const [isDictating, setIsDictating] = useState(false);

        const {
            isLoading: isTranscribing,
            mutateAsync: transcribeAudio,
        } = useMultipartMutation<FormData, { transcription?: any }>({
            method: 'POST',
            pathname: `/agents/${agentId}/transcribe`,
        });

        const setInputValue = (text: string) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            // eslint-disable-next-line max-len
            const textarea = inputRef.current.fieldRef.current.textareaRef.current;
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
                window.HTMLTextAreaElement.prototype,
                'value',
            ).set;
            nativeInputValueSetter?.call(textarea, text);
            const event = new Event('input', { bubbles: true });
            textarea.dispatchEvent(event);
            setTimeout(() => inputRef.current?.focus(), 0);
        };

        const handleDictateAccept = useCallback(async (blob: Blob | null) => {
            setIsDictating(false);
            if (blob) {
                try {
                    const formData = new FormData();
                    formData.append('audio', blob);
                    const { transcription } = await transcribeAudio({
                        body: formData,
                    });
                    if (transcription) {
                        onChange(transcription);
                    }
                } catch (error) {
                // Error handling is done by the parent component
                }
            }
        }, [transcribeAudio, onChange]);

        const handleDictateClick = useCallback(() => setIsDictating(true), []);
        const handleDictateCancel = useCallback(() => setIsDictating(false), []);

        const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
            // Allow Enter for new lines, but prevent form submission
            if (event.key === 'Enter' && event.ctrlKey) {
                // Ctrl+Enter could be used for submission if needed
            }
        };

        useImperativeHandle(ref, () => ({
            focus: () => {
                inputRef.current?.focus();
            },
        }));

        const disableElements = isTranscribing || disabled;

        if (isDictating) {
            return (
                <DictateForm
                    onAccept={handleDictateAccept}
                    onCancel={handleDictateCancel}
                />
            );
        }

        return (
            <Container>
                <InputContainer>
                    <MessageInput
                        variant="shaded"
                        placeholder={placeholder}
                        value={value}
                        grow
                        rows={5}
                        onChange={(event) => onChange(event.target.value)}
                        onKeyDown={handleKeyDown}
                        ref={inputRef}
                        disabled={disableElements}
                    />
                    <RenderIf isTrue={attachedFiles.length > 0}>
                        <AttachedFiles
                            files={attachedFiles}
                            onRemoveFile={onRemoveFile}
                            disabled={disableElements}
                        />
                    </RenderIf>
                    <ActionsContainer>
                        <AttachFileButton
                            acceptedFormats={IMAGE_FORMATS_ACCEPTED_STRING}
                            onAttachFiles={onAttachFiles}
                            disabled={disableElements}
                        />
                        <FrequentMessage
                            context={mode}
                            onSelectMessage={setInputValue}
                            agentId={agentId}
                            disabled={disableElements}
                        />
                        <ActionButton
                            tooltip="Dictate"
                            icon={<MicrophoneIcon />}
                            onClick={handleDictateClick}
                            disabled={disableElements}
                        />
                        <RenderIf isTrue={!isEmpty(value)}>
                            <HelpMeWrite
                                value={value}
                                onInsert={setInputValue}
                                agentId={agentId}
                                disabled={disableElements}
                            />
                        </RenderIf>
                    </ActionsContainer>
                </InputContainer>
            </Container>
        );
    },
);

FeedbackInput.displayName = 'FeedbackInput';

export default FeedbackInput;
